import { c as _c } from "react/compiler-runtime";
import { useQuery } from "@tanstack/react-query";
import { getAllExchanges, getCoinExchangePdp, getCoinPdp, getExchangeNames, getExchangePdp } from "@/app/apiServices/market/apiCalls";
import { MARKET_QUERY_KEYS, MARKET_QUERY_OPTIONS } from "@/app/apiServices/market/constants";
import { safeParseJson } from "@/app/utils/safeJson";
type ExchangeNames = Record<string, {
  name?: string;
  name_fa?: string;
}>;
let exchangeNamesPromise: Promise<ExchangeNames> | null = null;
const getCachedExchangeNames = () => {
  if (exchangeNamesPromise) return exchangeNamesPromise;
  exchangeNamesPromise = (async () => {
    const cached = typeof window === "undefined" ? null : sessionStorage.getItem("exchanges");
    const cachedExchangeNames = safeParseJson<ExchangeNames>(cached, {});
    if (Object.keys(cachedExchangeNames).length) return cachedExchangeNames;
    const exchangeNames = (await getExchangeNames()) as ExchangeNames;
    if (typeof window !== "undefined" && Object.keys(exchangeNames).length) {
      sessionStorage.setItem("exchanges", JSON.stringify(exchangeNames));
    }
    return exchangeNames;
  })();
  return exchangeNamesPromise;
};
export const getExchangeName = async (exchange: string, lang: string) => {
  try {
    const exchangeNames = await getCachedExchangeNames();
    const exchangeData = exchangeNames[exchange];
    if (!exchangeData) return exchange;
    return lang === "fa" ? exchangeData.name_fa ?? exchange : exchangeData.name ?? exchange;
  } catch (error) {
    console.error("Error fetching exchange name:", error);
    return exchange;
  }
};
export const useAllExchangesQuery = coin => {
  const $ = _c(10);
  let normalizedCoin;
  let t0;
  let t1;
  if ($[0] !== coin) {
    normalizedCoin = coin?.toUpperCase();
    t1 = useQuery;
    t0 = MARKET_QUERY_KEYS.allExchanges(normalizedCoin);
    $[0] = coin;
    $[1] = normalizedCoin;
    $[2] = t0;
    $[3] = t1;
  } else {
    normalizedCoin = $[1];
    t0 = $[2];
    t1 = $[3];
  }
  let t2;
  if ($[4] !== normalizedCoin) {
    t2 = () => getAllExchanges(normalizedCoin as string);
    $[4] = normalizedCoin;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const t3 = !!normalizedCoin;
  let t4;
  if ($[6] !== t0 || $[7] !== t2 || $[8] !== t3) {
    t4 = {
      queryKey: t0,
      queryFn: t2,
      enabled: t3,
      select: _temp,
      ...MARKET_QUERY_OPTIONS
    };
    $[6] = t0;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t1(t4);
};
export const useCoinPdpQuery = symbol => {
  const $ = _c(10);
  let normalizedSymbol;
  let t0;
  let t1;
  if ($[0] !== symbol) {
    normalizedSymbol = symbol?.toUpperCase();
    t1 = useQuery;
    t0 = MARKET_QUERY_KEYS.coinPdp(normalizedSymbol);
    $[0] = symbol;
    $[1] = normalizedSymbol;
    $[2] = t0;
    $[3] = t1;
  } else {
    normalizedSymbol = $[1];
    t0 = $[2];
    t1 = $[3];
  }
  let t2;
  if ($[4] !== normalizedSymbol) {
    t2 = () => getCoinPdp(normalizedSymbol as string);
    $[4] = normalizedSymbol;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const t3 = Boolean(normalizedSymbol);
  let t4;
  if ($[6] !== t0 || $[7] !== t2 || $[8] !== t3) {
    t4 = {
      queryKey: t0,
      queryFn: t2,
      enabled: t3,
      select: _temp2,
      ...MARKET_QUERY_OPTIONS
    };
    $[6] = t0;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t1(t4);
};
export const useExchangePdpQuery = exchange => {
  const $ = _c(10);
  let normalizedExchange;
  let t0;
  let t1;
  if ($[0] !== exchange) {
    normalizedExchange = exchange?.toUpperCase();
    t1 = useQuery;
    t0 = MARKET_QUERY_KEYS.exchangePdp(normalizedExchange);
    $[0] = exchange;
    $[1] = normalizedExchange;
    $[2] = t0;
    $[3] = t1;
  } else {
    normalizedExchange = $[1];
    t0 = $[2];
    t1 = $[3];
  }
  let t2;
  if ($[4] !== normalizedExchange) {
    t2 = () => getExchangePdp(normalizedExchange as string);
    $[4] = normalizedExchange;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const t3 = Boolean(normalizedExchange);
  let t4;
  if ($[6] !== t0 || $[7] !== t2 || $[8] !== t3) {
    t4 = {
      queryKey: t0,
      queryFn: t2,
      enabled: t3,
      select: _temp3,
      ...MARKET_QUERY_OPTIONS
    };
    $[6] = t0;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t1(t4);
};
export const useExchangeNamesQuery = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      queryKey: MARKET_QUERY_KEYS.exchangeNames,
      queryFn: getExchangeNames,
      ...MARKET_QUERY_OPTIONS
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return useQuery(t0);
};
export const useCoinExchangePdpQuery = (exchange, coin) => {
  const $ = _c(13);
  let normalizedCoin;
  let normalizedExchange;
  let t0;
  let t1;
  if ($[0] !== coin || $[1] !== exchange) {
    normalizedExchange = exchange?.toUpperCase();
    normalizedCoin = coin?.toUpperCase();
    t1 = useQuery;
    t0 = MARKET_QUERY_KEYS.coinExchangePdp(normalizedExchange, normalizedCoin);
    $[0] = coin;
    $[1] = exchange;
    $[2] = normalizedCoin;
    $[3] = normalizedExchange;
    $[4] = t0;
    $[5] = t1;
  } else {
    normalizedCoin = $[2];
    normalizedExchange = $[3];
    t0 = $[4];
    t1 = $[5];
  }
  let t2;
  if ($[6] !== normalizedCoin || $[7] !== normalizedExchange) {
    t2 = () => getCoinExchangePdp(normalizedExchange as string, normalizedCoin as string);
    $[6] = normalizedCoin;
    $[7] = normalizedExchange;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  const t3 = Boolean(normalizedExchange && normalizedCoin);
  let t4;
  if ($[9] !== t0 || $[10] !== t2 || $[11] !== t3) {
    t4 = {
      queryKey: t0,
      queryFn: t2,
      enabled: t3,
      select: _temp4,
      ...MARKET_QUERY_OPTIONS
    };
    $[9] = t0;
    $[10] = t2;
    $[11] = t3;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  return t1(t4);
};
function _temp(data) {
  return data?.message ?? [];
}
function _temp2(data) {
  return data.message ?? [];
}
function _temp3(data) {
  return data.message ?? {};
}
function _temp4(data) {
  return data.message ?? {};
}